import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { PublicLayout } from "@/components/shared/PublicLayout";
import { getBlogPost } from "@/lib/api";
import { isPastEvent } from "@/components/shared/StoreCommunity";
import { Calendar, CalendarDays, Eye, Tag, ArrowLeft, User, Info, Store, UserPlus, Mail, ArrowRight, MapPin } from "lucide-react";

const fmtEvent = (s) => new Date(s).toLocaleString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });

const EventBox = ({ post }) => {
  const past = isPastEvent(post);
  return (
    <div className={`mb-8 rounded-xl border p-5 ${past ? "border-slate-200 bg-slate-50" : "border-teal/40 bg-teal/5"}`} data-testid="blog-event-box">
      <span className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full mb-3 ${past ? "bg-slate-200 text-slate-600" : "bg-teal text-white"}`} data-testid="blog-event-status">
        {past ? "Past event — this event has ended" : "Upcoming event"}
      </span>
      <p className="flex items-center gap-2 text-navy font-semibold"><CalendarDays className="w-4 h-4 text-teal" />{fmtEvent(post.event_start)}{post.event_end ? ` – ${fmtEvent(post.event_end)}` : ""}</p>
      {post.location_slug && (
        <Link to={`/locations/${post.location_slug}`} className="mt-2 flex items-center gap-2 text-teal hover:underline text-sm" data-testid="blog-event-store-link">
          <MapPin className="w-4 h-4" />{post.location_name || "Visit the store page"}
        </Link>
      )}
    </div>
  );
};

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const data = await getBlogPost(slug);
        setPost(data);
      } catch (err) {
        setError("Post not found");
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <PublicLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal"></div>
        </div>
      </PublicLayout>
    );
  }

  if (error || !post) {
    return (
      <PublicLayout>
        <div className="min-h-screen flex flex-col items-center justify-center gap-4">
          <p className="text-slate-500 text-lg font-body">{error || "Post not found"}</p>
          <Link to="/blog" className="text-teal hover:underline font-body flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="bg-navy py-16" data-testid="blog-post-hero">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/blog"
            className="inline-flex items-center gap-1 text-teal hover:text-teal-light text-sm font-body mb-6"
            data-testid="blog-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>
          {post.category && (
            <span className="inline-block px-3 py-1 bg-teal/20 text-teal rounded-full text-sm font-medium mb-4">
              {post.category.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
            </span>
          )}
          <h1 className="font-heading font-bold text-3xl lg:text-4xl text-white mb-4" data-testid="blog-post-title">
            {post.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-slate-400 text-sm font-body">
            {post.author && (
              <span className="flex items-center gap-1">
                <User className="w-4 h-4" /> {post.author}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {new Date(post.publish_date || post.created_at).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </span>
            <span className="flex items-center gap-1">
              <Eye className="w-4 h-4" /> {post.views || 0} views
            </span>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      {post.featured_image && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
          <img
            src={post.featured_image}
            alt={post.title}
            className="w-full rounded-xl shadow-lg object-cover max-h-[400px]"
            data-testid="blog-post-image"
          />
        </div>
      )}

      {/* Content */}
      <section className="py-12 bg-slate-50" data-testid="blog-post-content">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl p-6 lg:p-10 shadow-sm border border-slate-200">
            {post.event_start && <EventBox post={post} />}
            <div
              className="prose prose-slate max-w-none font-body text-slate-700 leading-relaxed"
              data-testid="blog-post-body"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="mt-8 pt-6 border-t border-slate-200">
                <div className="flex flex-wrap items-center gap-2">
                  <Tag className="w-4 h-4 text-slate-400" />
                  {post.tags.filter((t) => !t.startsWith("auto:")).map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-slate-100 text-slate-600 text-sm rounded-full font-body"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* CTA Links */}
          <div className="mt-8" data-testid="blog-post-cta-links">
            <h3 className="font-heading font-bold text-xl text-navy mb-4">Take the Next Step</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { to: "/about", label: "About", desc: "Who we are", icon: Info, testId: "blog-cta-about" },
                { to: "/open-a-store", label: "Open a Store", desc: "Launch your location", icon: Store, testId: "blog-cta-open-store" },
                { to: "/become-dealer", label: "Become a Dealer", desc: "Partner with us", icon: UserPlus, testId: "blog-cta-become-dealer" },
                { to: "/contact", label: "Contact Us", desc: "Get in touch", icon: Mail, testId: "blog-cta-contact" },
              ].map((cta) => {
                const Icon = cta.icon;
                return (
                  <Link
                    key={cta.to}
                    to={cta.to}
                    data-testid={cta.testId}
                    className="group bg-white border border-slate-200 hover:border-teal hover:shadow-md rounded-xl p-4 flex flex-col gap-2 transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <div className="w-9 h-9 rounded-md bg-teal/10 text-teal flex items-center justify-center">
                        <Icon className="w-4 h-4" />
                      </div>
                      <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-teal group-hover:translate-x-0.5 transition-all" />
                    </div>
                    <div>
                      <p className="font-heading font-bold text-navy">{cta.label}</p>
                      <p className="text-sm text-slate-500 font-body">{cta.desc}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
